/*
 * KSTYLESHOP wishlist: product handles saved in the shopper's browser (localStorage).
 * - Buttons with data-kstyle-wish="<handle>" toggle a product (cards, product page).
 * - Elements with data-kstyle-wish-count show how many are saved (header).
 * - [data-kstyle-wishlist] (the wishlist page) shows its saved items, latest first.
 * Works without an account; if storage is unavailable the list lasts for the visit.
 */
(() => {
  const KEY = 'kstyle:wishlist';
  let memory = [];

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
    } catch {
      return memory;
    }
  };

  const write = (list) => {
    memory = list;
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch {}
  };

  let list = read();

  const syncButtons = (root = document) => {
    root.querySelectorAll('[data-kstyle-wish]').forEach((button) => {
      const saved = list.includes(button.dataset.kstyleWish);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      const title = saved ? button.dataset.titleSaved : button.dataset.titleAdd;
      if (title) button.setAttribute('aria-label', title);
      const label = button.querySelector('.kstyle-wish__label');
      const text = saved ? button.dataset.labelSaved : button.dataset.labelAdd;
      if (label && text && label.textContent !== text) label.textContent = text; // unchanged text: no DOM write, no observer loop
    });
  };

  const syncCount = () => {
    document.querySelectorAll('[data-kstyle-wish-count]').forEach((count) => {
      count.textContent = list.length > 99 ? '99+' : String(list.length);
      count.hidden = list.length === 0;
    });
  };

  const syncPage = () => {
    document.querySelectorAll('[data-kstyle-wishlist]').forEach((page) => {
      const items = [...page.querySelectorAll('[data-kstyle-wishlist-item]')];
      const byHandle = new Map(items.map((item) => [item.dataset.kstyleWishlistItem, item]));
      let shown = 0;
      items.forEach((item) => (item.hidden = true));
      list.forEach((handle) => {
        const item = byHandle.get(handle);
        if (!item) return;
        item.parentElement.appendChild(item); // saved order, latest first
        item.hidden = false;
        shown += 1;
      });
      const empty = page.querySelector('[data-kstyle-wishlist-empty]');
      if (empty) empty.hidden = shown > 0;
      page.classList.toggle('kstyle-wishlist--empty', shown === 0);
    });
  };

  const sync = () => {
    syncButtons();
    syncCount();
    syncPage();
  };

  // Capture phase, so a heart on a card never opens the product or a quick-add.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-kstyle-wish]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      const handle = button.dataset.kstyleWish;
      list = read();
      list = list.includes(handle) ? list.filter((h) => h !== handle) : [handle, ...list];
      write(list);
      sync();
    },
    true
  );

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key !== KEY) return;
    list = read();
    sync();
  });

  // Cards added later (recommendations, search, load more) get their state too.
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      syncButtons();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }
})();
